import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { renderAsync } from "docx-preview";

/**
 * Modal xem nhanh 1 file hợp đồng đã merge (docx/pdf/ảnh).
 *
 * Tách riêng khỏi PreviewFile.js: component đó render dạng lưới thẻ có nút upload/xoá,
 * không nhúng vào ô bảng được. Ở đây chỉ cần mở xem theo URL.
 *
 * File trên FTP nằm sau endpoint download-image yêu cầu auth => phải fetch kèm Bearer token.
 * Riêng pdf/ảnh dùng <iframe>/<img> không gắn được header, nên cũng tải blob rồi tạo object URL.
 */
export default function ModalXemFile({ show, onHide, url, title }) {
    const { user } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [blobUrl, setBlobUrl] = useState("");
    const containerRef = useRef(null);

    // URL có dạng .../download-image?url=hopdong/{code}/Y/m/ten-file.docx nên đuôi file nằm
    // TRONG query string. Cắt ở dấu '?' sẽ mất đuôi => phải đọc tham số url trước, chỉ khi
    // không có mới rơi về phần path.
    const lower = (() => {
        const raw = url || "";
        const match = raw.match(/[?&]url=([^&]+)/i);
        const target = match ? decodeURIComponent(match[1]) : raw.split("?")[0];
        return target.toLowerCase();
    })();
    const isImage = /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(lower);
    const isPdf = lower.endsWith(".pdf");
    const isOffice = /\.(doc|docx|xls|xlsx)$/.test(lower);

    useEffect(() => {
        if (!show || !url) return;
        let cancelled = false;
        let createdUrl = "";
        setLoading(true);
        setError("");

        fetch(url, { headers: user.config.headers })
            .then((res) => {
                if (!res.ok) throw new Error("Không thể tải file (" + res.status + ")");
                return isOffice ? res.arrayBuffer() : res.blob();
            })
            .then((payload) => {
                if (cancelled) return null;
                if (isOffice) {
                    // Container chỉ tồn tại sau khi Modal render xong phần body.
                    if (!containerRef.current) throw new Error("Không mở được khung xem file");
                    containerRef.current.innerHTML = "";
                    return renderAsync(payload, containerRef.current, null, {
                        className: "docx-preview-container",
                        inWrapper: true,
                        ignoreWidth: false,
                        ignoreHeight: false,
                        ignoreFonts: false,
                        breakPages: true,
                        renderHeaders: true,
                        renderFooters: true,
                        renderFootnotes: true,
                        useBase64URL: true,
                    });
                }
                createdUrl = URL.createObjectURL(payload);
                setBlobUrl(createdUrl);
                return null;
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            // Thu hồi object URL để không rò bộ nhớ khi đóng/mở nhiều lần.
            if (createdUrl) URL.revokeObjectURL(createdUrl);
            setBlobUrl("");
        };
        // user.config bỏ khỏi deps: là object từ Redux, chỉ cần giá trị tại thời điểm fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, url, isOffice]);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6 mb-0">{title || "Xem file hợp đồng"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0" style={{ height: "80vh" }}>
                {loading && (
                    <div className="w-100 d-flex align-items-center justify-content-center pt-4">
                        <span className="spinner-border text-primary mr-2"></span> Đang tải file...
                    </div>
                )}
                {error && <div className="alert alert-danger m-3">{error}</div>}

                {/* Container docx phải luôn có mặt trong DOM để ref sẵn sàng khi fetch xong. */}
                <div
                    className="w-100 h-100 overflow-auto bg-secondary"
                    style={{ padding: "20px 0", display: isOffice && !error ? "block" : "none" }}
                >
                    <div ref={containerRef}></div>
                </div>

                {!isOffice && !error && blobUrl && (
                    isImage ? (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                            <img src={blobUrl} alt="" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                        </div>
                    ) : isPdf ? (
                        <iframe src={blobUrl} width="100%" height="100%" style={{ border: "none" }} title="file preview" />
                    ) : (
                        <div className="h-100 d-flex flex-column align-items-center justify-content-center">
                            <p>Định dạng này không hỗ trợ xem trực tiếp.</p>
                            <a href={blobUrl} download className="btn btn-success">
                                Tải file về máy
                            </a>
                        </div>
                    )
                )}
            </Modal.Body>
        </Modal>
    );
}
